import { getAudioContext, getMasterGain } from "./ctx";

/* Standard Web Audio lookahead-scheduler pattern: the audio clock is the
   source of truth (click times are scheduled exactly, so there's no
   setInterval drift), while onBeat fires close to real playback time via
   its own short setTimeout — good enough for visual sync, not used for
   anything audio-critical. */

const LOOKAHEAD_MS = 25;
const SCHEDULE_AHEAD_S = 0.1;

export type MetronomeOptions = {
  bpm: number;
  accentEvery?: number;
  onBeat?: (accent: boolean) => void;
  volume?: number;
};

export class Metronome {
  private bpm: number;
  private accentEvery: number;
  private onBeat?: (accent: boolean) => void;
  private volume: number;
  private timerId: number | null = null;
  private nextNoteTime = 0;
  private beatCount = 0;
  private running = false;

  constructor(opts: MetronomeOptions) {
    this.bpm = opts.bpm;
    this.accentEvery = opts.accentEvery ?? 4;
    this.onBeat = opts.onBeat;
    this.volume = opts.volume ?? 1;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    const ctx = getAudioContext();
    this.beatCount = 0;
    this.nextNoteTime = ctx.currentTime + 0.05;
    this.scheduler();
  }

  stop(): void {
    this.running = false;
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  setBpm(bpm: number): void {
    this.bpm = bpm;
  }

  setVolume(v: number): void {
    this.volume = v;
  }

  /** Audio-clock time (seconds) of the next scheduled beat. */
  nextBeatTime(): number {
    return this.nextNoteTime;
  }

  private scheduler = (): void => {
    if (!this.running) return;
    const ctx = getAudioContext();
    while (this.nextNoteTime < ctx.currentTime + SCHEDULE_AHEAD_S) {
      const accent = this.beatCount % this.accentEvery === 0;
      this.playClick(this.nextNoteTime, accent);
      if (this.onBeat) {
        const delayMs = Math.max(0, (this.nextNoteTime - ctx.currentTime) * 1000);
        setTimeout(() => this.onBeat?.(accent), delayMs);
      }
      this.nextNoteTime += 60 / this.bpm;
      this.beatCount += 1;
    }
    this.timerId = window.setTimeout(this.scheduler, LOOKAHEAD_MS);
  };

  private playClick(time: number, accent: boolean): void {
    if (this.volume <= 0) return;
    const ctx = getAudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = accent ? 1600 : 1200;
    const peak = (accent ? 0.35 : 0.22) * this.volume;
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime(peak, time + 0.002);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.05);
    osc.connect(gain).connect(getMasterGain());
    osc.start(time);
    osc.stop(time + 0.06);
  }
}
